import type { OrderStatus } from '@/api/endpoints/orders';
import type { OrderListView } from '../hooks/useOrderListFilters';

/** Statuses shown in the list filter when the Active view tab is selected. */
export const ORDER_LIST_ACTIVE_STATUSES: readonly OrderStatus[] = [
  'New',
  'In-Process',
  'Paused',
] as const;

/** Non-SSI callers cannot filter to `New` (FR-R008). */
export const ORDER_LIST_ACTIVE_STATUSES_NON_SSI: readonly OrderStatus[] = [
  'In-Process',
  'Paused',
] as const;

/** Statuses shown in the list filter when the Inactive view tab is selected. */
export const ORDER_LIST_INACTIVE_STATUSES: readonly OrderStatus[] = [
  'Completed',
  'Cancelled',
  'Closed',
] as const;

/** Status multi-select options for the current view tab (All shows every status). */
export function orderListStatusOptionsForView(
  view: OrderListView,
  isSsi: boolean,
): readonly OrderStatus[] {
  const activeStatuses = isSsi
    ? ORDER_LIST_ACTIVE_STATUSES
    : ORDER_LIST_ACTIVE_STATUSES_NON_SSI;
  if (view === 'inactive') return ORDER_LIST_INACTIVE_STATUSES;
  if (view === 'all') {
    return [...activeStatuses, ...ORDER_LIST_INACTIVE_STATUSES];
  }
  return activeStatuses;
}

/**
 * Count of top-level filter groups the bar renders for a role: SSI sees all 8
 * (adds Company, Assigned To, Priority, Subscription); non-SSI sees 4.
 */
export function countOrderListFilterGroups(isSsi: boolean): number {
  return isSsi ? 8 : 4;
}

/** Drop status selections that conflict with the selected view tab. */
export function pruneOrderListStatusForView(
  view: OrderListView,
  status: string[] | undefined,
  isSsi: boolean,
): string[] | undefined {
  if (!status?.length) return undefined;
  const allowed = new Set<string>(orderListStatusOptionsForView(view, isSsi));
  const next = status.filter((value) => allowed.has(value));
  return next.length ? next : undefined;
}
